import React, { useState } from 'react';
import { Col, Row, Select } from 'antd';
import { Span, Div, Icon } from 'ui';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withRouter } from 'utils/withRouter';

const { Option } = Select;

const ListIcon = styled.i`
  padding-top: 6px;
  font-size: 16px;
  cursor: pointer;
  color: ${p => p.theme.colors.theme}
  ${props =>
    props.link &&
    css`
      cursor: pointer;
      color: #108ee9;
    `}
`;

const ListContent = styled(Col)`
    text-decoration: underline;
    cursor: pointer;
    color: #108ee9;
  ${props =>
    props.ellipsis &&
    css`
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}
`;

const StyledSpan = styled.span`
    color: ${p => p.theme.colors.theme}
`;

const StyledIcon = styled(Icon)`
    margin-right: 5px;
`;

const TextCol = styled(Col)`
    width: auto !important;
`;

const StyledSelect = styled(Select)`
    font-size: 16px;
    font-weight: 400;
    width: 100%;
`;

/**
 *  Component for listview
 *
 * @param {array} items - array of items for the list, should contain { id, title, uri }
 * @param {function} addToList - function for adding an item to the list
 * @param {string} addToListPlaceholder - placeholder string for inputfield
 * @param {string} addToListType - type of input for adding an item ('string', 'search')
 * @param {function} removeFromList - function for removing an item to the list
 * @param {string} icon - icon for type list
 * @param {boolean} ellipsis - true or false when content needs to be truncated with ...
 */
const LinkedList = ({
  items = [],
  disabled = false,
  icon,
  ellipsis = false,
  addToListPlaceholder,
  addToListType,
  options = [],
  setItems,
  history,
  shouldHandleOnClick = false,
  onSearch,
  isLoading = false,
  optionKey = 'objectId',
  optionValue = 'description',
  itemKey = 'objectId',
  itemValue = 'description',
}) => {
  // eslint-disable-next-line
  const [inputValue, setInputValue] = useState(null);

  const getLinkedObjectOptions = () => {
    if (options?.length > 0) {
      return options?.filter(o => !items?.find(so => o[optionKey] === so[itemKey]))
        ?.map(option =>
          <Option key={option[optionKey]}>
            <StyledSpan>
              <StyledIcon type={option.icon} />
              {option.type && `${option.type}:`}
            </StyledSpan>
            {option[optionValue].includes(`${option.type}:`) ?
              option[optionValue].replace(`${option.type}:`, '') :
              option[optionValue]
            }
          </Option>);
    }
    return null;
  };

  const handleSelect = (e) => {
    if (onSearch) {
      onSearch(null);
    }
    const selectedItem = options?.find(o => o[optionKey] === e);
    setItems(
      [
        ...items,
        {
          ...selectedItem,
          [itemKey]: e,
        },
      ],
    );
  };

  return (
    <>
      {items?.length > 0 &&
        items?.map((item, key) =>
          <Row key={key} gutter={12}>
            {icon &&
              <Col span={2}>
                <ListIcon className={`fas fa-${item?.icon || icon}`} />
              </Col>
            }
            {shouldHandleOnClick ?
              <ListContent
                ellipsis={ellipsis}
                span={20}
                onClick={() => history.push(`/${item.type}/${item.objectId}`)}
              >
                <Span data-test-id={`ticket-linked-object-${item.type}`}>
                  {item[itemValue]}
                </Span>
              </ListContent> :
              <TextCol span={20}>
                <Span>{item[itemValue]}</Span>
              </TextCol>
            }
            {!item.isSourceObject && !disabled &&
              <Col span={2}>
                <ListIcon
                  link
                  className='far fa-trash-alt'
                  onClick={() => setItems(
                    items?.filter(o => o[itemKey] !== item[itemKey]),
                  )}
                  data-test-id={`btn-ticket-linked-object-${item.type}-trash`}
                />
              </Col>
            }
          </Row>,
        )}
      {setItems &&
        <Div marginTop={10}>
          <Row gutter={12} type='flex' align='middle'>
            <Col span={2}>
              <ListIcon link={!!inputValue} className='fas fa-plus' />
            </Col>
            <Col span={22}>
              {addToListType && addToListType === 'string' &&
                <StyledSelect
                  disabled={disabled}
                  showSearch={true}
                  value={null}
                  placeholder={addToListPlaceholder}
                  onSearch={onSearch}
                  filterOption={!onSearch}
                  notFoundContent={null}
                  onSelect={handleSelect}
                  showArrow={false}
                  loading={isLoading}
                >
                  {getLinkedObjectOptions()}
                </StyledSelect>
              }
            </Col>
          </Row>
        </Div>
      }
    </>
  );
};

LinkedList.propTypes = {
  items: PropTypes.array,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  ellipsis: PropTypes.bool,
  addToListPlaceholder: PropTypes.string,
  addToListType: PropTypes.string,
  options: PropTypes.array,
  setItems: PropTypes.func,
  history: PropTypes.object,
  onSearch: PropTypes.func,
  shouldHandleOnClick: PropTypes.bool,
  isLoading: PropTypes.bool,
  optionKey: PropTypes.string,
  optionValue: PropTypes.string,
  itemKey: PropTypes.string,
  itemValue: PropTypes.string,
};

export default withRouter(LinkedList);
